import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to handle browser back button for modals.
 * When a modal opens, it pushes a history entry.
 * When the back button is pressed, it closes the modal instead of navigating away.
 * 
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Function to call when the modal should close
 * @param {string} modalId - Unique identifier for this modal (used in history state)
 */
export const useModalHistory = (isOpen, onClose, modalId = 'modal') => {
  const historyPushed = useRef(false);
  const onCloseRef = useRef(onClose);
  
  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const handlePopState = () => {
      // Modal is being closed via back button
      if (historyPushed.current) {
        historyPushed.current = false;
        onCloseRef.current();
      }
    };

    if (isOpen && !historyPushed.current) {
      // Push a new history entry when modal opens
      window.history.pushState({ modal: modalId }, '', window.location.href);
      historyPushed.current = true;
      
      // Listen for back button
      window.addEventListener('popstate', handlePopState);
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, modalId]);

  // Reset when modal closes externally
  useEffect(() => {
    if (!isOpen) {
      historyPushed.current = false;
    }
  }, [isOpen]);

  // Function to properly close modal
  const closeModal = useCallback(() => {
    if (historyPushed.current) {
      // Go back in history, which will trigger popstate and call onClose
      window.history.back();
    } else {
      // Directly call onClose if history wasn't pushed
      onCloseRef.current();
    }
  }, []);

  return { closeModal };
};

export default useModalHistory;
